// ByExpiryCard — renderer for viz: by_expiry. A grouped term-structure table:
// one row per expiry (sorted ascending by expiry_date), with the leading NET
// diverging bar, then three super-columns — PREMIUM · NOTIONAL · TRADES — each
// split into Total / Call / Put under a two-level header, and a trailing
// single-column OPEN INTEREST (total contracts). Total is bold; the Call/Put
// split reads muted. Notional, the call/put trade splits and open interest are
// optional fields — cells fall back to `-` until flow_claude emits them.

import { Fragment } from 'react';
import { Box } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  fmtCount,
  fmtExpiryChipDate,
  fmtNotional,
  fmtUsd,
  FlowMetric,
  maxAbs,
  METRIC_LABELS,
  METRIC_ORDER,
  resolveNotionalUnit,
} from '../../../../providers/options-flow/options-flow.helpers';
import { ByExpiryReport, ByExpiryRow } from '../../../../providers/options-flow/types';
import { FlowCard } from '../flow-card';
import { FlowEmptyState } from '../flow-empty-state';
import { DivergingBar } from '../diverging-bar';
import { flowTableSx, tdMonoSx, tdNumSx, tdSx, thNumSx, thSx } from '../table-chrome';

interface ByExpiryCardProps {
  report: ByExpiryReport;
}

// Vertical divider at each super-column boundary — applied to the group's
// leading cell (super-header, the Total sub-header, and each row's Total cell)
// so the line runs continuously down the table.
const groupDivider = { borderLeft: `1px solid ${tokens.border.default}` };

const superThSx = { ...thSx, textAlign: 'center' as const };
const tdTotalSx = { ...tdNumSx, fontWeight: 700, ...groupDivider };
const tdSplitSx = { ...tdNumSx, fontWeight: 400, color: tokens.text.muted };
// Trailing standalone Open Interest column — divider-led to fence it off from
// the Trades triad, weighted like a single metric (as Net is).
const tdOiSx = { ...tdNumSx, ...groupDivider };

// `-` for a missing field (until the notional / call-put-trades splits land),
// else the formatter. Distinct from the formatters' own em-dash for null.
const cell = (value: number | null | undefined, fmt: (v: number) => string): string =>
  value == null || Number.isNaN(value) ? '-' : fmt(value);

// Super-column groups, in canonical METRIC_ORDER (Notional · Premium · Trades).
// Each maps its metric id to the row's Total / Call / Put fields; the formatter
// resolves at render (notional needs the unit).
interface ExpiryGroup {
  metric: FlowMetric;
  total: keyof ByExpiryRow;
  call: keyof ByExpiryRow;
  put: keyof ByExpiryRow;
}
const EXPIRY_GROUPS: ExpiryGroup[] = METRIC_ORDER.map((metric) => {
  const fields: Record<FlowMetric, Omit<ExpiryGroup, 'metric'>> = {
    notional: { total: 'notional', call: 'call_notional', put: 'put_notional' },
    premium: { total: 'premium_usd', call: 'call_premium_usd', put: 'put_premium_usd' },
    trades: { total: 'trades', call: 'call_trades', put: 'put_trades' },
  };
  return { metric, ...fields[metric] };
});

const SuperHeader = () => (
  <>
    <Box component="tr">
      <Box component="th" scope="col" rowSpan={2} sx={thSx}>
        Expiry
      </Box>
      <Box component="th" scope="col" rowSpan={2} sx={thSx}>
        Net
      </Box>
      {EXPIRY_GROUPS.map((g) => (
        <Box
          key={g.metric}
          component="th"
          scope="colgroup"
          colSpan={3}
          sx={{ ...superThSx, ...groupDivider }}
        >
          {METRIC_LABELS[g.metric]}
        </Box>
      ))}
      <Box component="th" scope="col" rowSpan={2} sx={{ ...thNumSx, ...groupDivider }}>
        Open Interest
      </Box>
    </Box>
    <Box component="tr">
      {EXPIRY_GROUPS.map((g) => (
        <Fragment key={g.metric}>
          <Box component="th" scope="col" sx={{ ...thNumSx, ...groupDivider }}>
            Total
          </Box>
          <Box component="th" scope="col" sx={thNumSx}>
            Call
          </Box>
          <Box component="th" scope="col" sx={thNumSx}>
            Put
          </Box>
        </Fragment>
      ))}
    </Box>
  </>
);

const Row = ({ row, netScale, unit }: { row: ByExpiryRow; netScale: number; unit: string }) => {
  const fmtFor: Record<FlowMetric, (v: number) => string> = {
    notional: (v) => fmtNotional(v, unit),
    premium: fmtUsd,
    trades: fmtCount,
  };
  return (
    <Box component="tr">
      <Box component="td" sx={tdMonoSx}>
        {fmtExpiryChipDate(row.expiry_date)}
      </Box>
      <Box component="td" sx={tdSx}>
        <DivergingBar value={row.net_usd} max={netScale} width={90} height={12} />
      </Box>

      {EXPIRY_GROUPS.map((g) => {
        const fmt = fmtFor[g.metric];
        return (
          <Fragment key={g.metric}>
            <Box component="td" sx={tdTotalSx}>
              {cell(row[g.total] as number | null | undefined, fmt)}
            </Box>
            <Box component="td" sx={tdSplitSx}>
              {cell(row[g.call] as number | null | undefined, fmt)}
            </Box>
            <Box component="td" sx={tdSplitSx}>
              {cell(row[g.put] as number | null | undefined, fmt)}
            </Box>
          </Fragment>
        );
      })}

      <Box component="td" sx={tdOiSx}>
        {cell(row.oi, fmtCount)}
      </Box>
    </Box>
  );
};

export const ByExpiryCard = ({ report }: ByExpiryCardProps) => {
  // Copy before sort — never mutate the payload the provider holds.
  const rows = [...(report.rows ?? [])].sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));
  const netScale = maxAbs(rows.map((r) => r.net_usd));
  const unit = resolveNotionalUnit(report.meta);

  return (
    <FlowCard
      title={report.title}
      caption="Net taker flow by expiry · bar = net (buy ▸ / ◂ sell) · Total split into Call / Put"
    >
      {rows.length === 0 ? (
        <FlowEmptyState />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={flowTableSx}>
            <Box component="thead">
              <SuperHeader />
            </Box>
            <Box component="tbody">
              {rows.map((r) => (
                <Row key={r.expiry_date + r.expiry} row={r} netScale={netScale} unit={unit} />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </FlowCard>
  );
};
